import { useState, useEffect, useCallback, useRef } from 'react';
import { TokenManagementService, TokenMetadata } from '@/lib/token-management-service';

interface UseTokenMetadataReturn {
  tokenMetadata: TokenMetadata[];
  loading: boolean;
  error: string | null;
  refreshMetadata: () => Promise<void>;
  updateTokenMetadata: (symbol: string, metadata: Partial<TokenMetadata>, userId: string) => Promise<void>;
  toggleFeatured: (symbol: string, featured: boolean, userId: string) => Promise<void>;
}

export function useTokenMetadata(): UseTokenMetadataReturn {
  const [tokenMetadata, setTokenMetadata] = useState<TokenMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  
  // Use ref to prevent recreating service instance on every render
  const tokenServiceRef = useRef<TokenManagementService | null>(null);
  if (!tokenServiceRef.current) {
    tokenServiceRef.current = new TokenManagementService();
  }
  const tokenService = tokenServiceRef.current;

  const refreshMetadata = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      setError(null);
      const metadata = await tokenService.getTokenMetadata();
      setTokenMetadata(metadata);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load token metadata');
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  }, [tokenService, isRefreshing]);

  const updateTokenMetadata = useCallback(async (
    symbol: string, 
    metadata: Partial<TokenMetadata>, 
    userId: string
  ) => {
    try {
      await tokenService.updateTokenMetadata(symbol, metadata, userId);
      // Optimistically update local state instead of full refresh
      setTokenMetadata(prev => 
        prev.map(token => 
          token.symbol === symbol 
            ? { ...token, ...metadata, updatedAt: new Date().toISOString(), updatedBy: userId }
            : token
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update token metadata');
      throw err;
    }
  }, [tokenService]);

  const toggleFeatured = useCallback(async (
    symbol: string, 
    featured: boolean, 
    userId: string
  ) => {
    try {
      await tokenService.toggleFeatured(symbol, featured, userId);
      // Optimistically update local state instead of full refresh
      setTokenMetadata(prev => 
        prev.map(token => 
          token.symbol === symbol 
            ? { ...token, featured, updatedAt: new Date().toISOString(), updatedBy: userId }
            : token
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle featured status');
      throw err;
    }
  }, [tokenService]);

  // Load metadata on mount only
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      // Call refreshMetadata directly without dependency
      const loadMetadata = async () => {
        try {
          setLoading(true);
          setError(null);
          const metadata = await tokenService.getTokenMetadata();
          setTokenMetadata(metadata);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load token metadata');
        } finally {
          setLoading(false);
        }
      };
      loadMetadata();
    }
  }, []); // Empty dependency array

  return {
    tokenMetadata,
    loading,
    error,
    refreshMetadata,
    updateTokenMetadata,
    toggleFeatured,
  };
}

